import { GetStaticPropsResult, NextPage } from 'next';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import { Button } from '@prickle/next-ui';
import { RichText } from 'prismic-reactjs';
import { i18nConfig } from '../../next-i18next.config';
import { CareerProps, Locale } from '../../types';
import { getJobs } from '../../utils/prismic/jobs';
import getPrismicMenuByLocale from '../../utils/prismic/menu';
import getPrismicOptionsByLocale from '../../utils/prismic/options';
import { Layout } from '../../components';
import { REVALIDATION_TIME } from '../../utils/constants';
import Link from 'next/link';

const CareersPage: NextPage<CareerProps> = (props) => {
  const { t } = useTranslation('careers');
  return (
    <>
      <NextSeo title="Prickle | Careers" description="Prickle | Careers" />
      <Layout options={props.options} menu={props.menu}>
        <div className="font-body">
          <div className="grid justify-between gap-20 pt-12 bg-white md:pt-0 md:grid-cols-2">
            <div className="h-full px-6 mx-auto">
              <h1 className="my-10 font-medium text-center text-7xl lg:text-8xl font-heading md:text-left">
                {t('careersheader')}
              </h1>
              <p className="py-4 text-lg text-black">
                <Trans i18nKey="careerssubtitle" ns="careers"></Trans>
              </p>

              <p className="py-4 text-lg text-black">
                <Trans i18nKey="careersdescription1" ns="careers">
                  Busca tu oportunidad en los siguientes proyectos, envianos tu
                  curriculum a
                  <Link
                    href="mailto:hr@prickle.io"
                    className="text-primary"
                    target="_blank"
                  >
                    hr@prickle.io
                  </Link>
                  , responderemos a tu solicitud lo antes posible para concertar
                  una reunion y discutir los requisitos.
                </Trans>
              </p>
            </div>
            <div className="hidden h-full md:flex">
              <Image
                alt="Developer at work"
                width={1350}
                height={900}
                className="object-cover object-right h-full"
                src="https://images.prismic.io/prickle-website/3a715803-32f0-43fc-b80c-337c1dcc00e0_photo-1498677231914-50deb6ba4217.jpeg?auto=compress,format"
              ></Image>
            </div>
          </div>
          <div className="mx-auto my-16 max-w-7xl">
            <h2 className="mb-16 text-4xl font-medium text-center text-white uppercase lg:text-6xl font-heading">
              {t('careersjoblisting')}
            </h2>
            {props.jobs.length === 0 && (
              <p className="px-8 text-center text-white">
                {t('careersemptylist1')}{' '}
                <Link
                  href="mailto:hr@prickle.io"
                  className="font-medium text-red-500"
                  target="_blank"
                >
                  hr@prickle.io
                </Link>{' '}
                {t('careersemptylist2')}
              </p>
            )}
            {props.jobs.length > 0 && (
              <div className="grid gap-6 px-6 md:grid-cols-2">
                {props.jobs.map((job) => (
                  <Link href={`careers/${job.uid}`} key={job.uid}>
                    <div className="flex items-center justify-between px-8 py-6 rounded-lg shadow-lg bg-darkGray">
                      <div className="flex flex-col ">
                        <span
                          data-testid={`job-title-${job.uid}`}
                          className="text-xl font-medium text-white"
                        >
                          {RichText.asText(job.data.title)}
                        </span>
                        <span
                          data-testid={`job-excerpt-${job.uid}`}
                          className="text-white text-primary-100"
                        >
                          {' '}
                          {RichText.asText(job.data.excerpt)}
                        </span>
                      </div>
                      <div></div>
                      <div>
                        <Button
                          color="primary"
                          data-testid={`job-button-${job.uid}`}
                          className="px-4 py-2 text-sm font-medium transition-colors duration-150 rounded-lg shadow-lg"
                        >
                          {t('careersviewjob')}
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export const getStaticProps = async ({
  previewData,
  locale,
}: {
  locale: Locale;
  previewData: any;
}): Promise<GetStaticPropsResult<CareerProps>> => {
  const menu = await getPrismicMenuByLocale(locale, previewData);
  const options = await getPrismicOptionsByLocale(locale, previewData);
  const jobs = (await getJobs(locale)).results;
  return {
    revalidate: REVALIDATION_TIME,
    props: {
      ...(await serverSideTranslations(
        locale,
        ['careers', 'common'],
        i18nConfig,
      )),
      menu,
      options,
      jobs,
    },
  };
};

export default CareersPage;
